import { useCallback, useMemo, useState } from "react";
import ChatBot from "./ChatBot";
import {
  CategoryType,
  MonthEnum,
  PadithiEnum,
  PredictionType,
} from "../utils/enums";
import { UserCharts } from "./user-charts";
import FilterForm from "./predictions/FilterForm";
import { createGetPredictionPayload } from "./predictions/prediction.util";
import { getSandboxUserPredictions } from "./predictions/predictionService";
import {
  getCurrentDayInTimezone,
  getMonthAccordingToBirthdayDay,
} from "../utils/dateFromat";
import { Box } from "@mui/material";
import Loading from "./loading/Loading";
import DataNotFound from "./not-found/DataNotFound";
import UserProfileModal from "./predictions/user-profile/UserProfileModal";
import AppDivider from "./divider/AppDivider";
import Predictions from "./predictions/prediction-view";
import { toast } from "react-toastify";

const PredictionPage = ({ userId, currentUser }) => {
  const [predictionsData, setPredictionData] = useState(null);

  const [isFetched, setIsFetched] = useState(false);

  const [loading, setLoading] = useState(false);

  const birthDate = currentUser?.birthDetails?.date;
  const birthTZ = currentUser?.birthDetails?.timezone;

  const userCurrentTimezone = currentUser?.userCurrentTimezone;

  const [filter, setFilter] = useState({
    conditionType: PadithiEnum.MASTER,
    category: CategoryType.PROMISE,
    predictionType: PredictionType.YEAR,
    month: MonthEnum.CURRENT_MONTH,
    day: getCurrentDayInTimezone(userCurrentTimezone),
  });

  const handleGetPredictions = useCallback(
    async (data) => {
      if (loading) return;
      let payload = createGetPredictionPayload(data);
      const userId = currentUser?._id;

      payload = { ...payload, userId, userCurrentTimezone };

      if (payload.month && payload.month === MonthEnum.CURRENT_MONTH)
        payload.month = getMonthAccordingToBirthdayDay(
          birthDate,
          userCurrentTimezone,
          birthTZ
        );

      if (payload.conditionType === PadithiEnum.LAL_KITAB) {
        if (
          payload.predictionType === PredictionType.MONTH &&
          (!payload.month || payload.day)
        )
          return;
        if (
          payload.predictionType === PredictionType.DAY &&
          (!payload.month || !payload.day)
        )
          return;
      }
      try {
        setFilter(data);
        setLoading(true);

        setPredictionData(null);
        const response = await getSandboxUserPredictions(payload);
        setPredictionData(response?.data);
      } catch (error) {
        toast.error(error.message || "Failed to load predictions");
      } finally {
        setLoading(false);
        if (!isFetched) setIsFetched(true);
      }
    },
    [currentUser, birthDate, birthTZ, userCurrentTimezone, isFetched, loading]
  );

  const showDataNotFound = useMemo(() => {
    if (!isFetched || loading) return false;

    const predictions = predictionsData?.predictions;
    if (!predictions) return true;

    if (Array.isArray(predictions) && predictions.length > 0) return false;

    const before = predictions.beforeMoonChange;
    const after = predictions.afterMoonChange;

    const isBeforeEmpty = !Array.isArray(before) || before.length === 0;
    const isAfterEmpty = !Array.isArray(after) || after.length === 0;

    return isBeforeEmpty && isAfterEmpty;
  }, [isFetched, loading, predictionsData]);


  return (
    <>
      <Box
        sx={{
          bgcolor: "background.paper",
          px: 0.5,
          borderRadius: 1,
          boxShadow: 1,
          py: 2,
        }}
      >
        {filter.conditionType !== PadithiEnum.MASTER && (
          <Box sx={{ mb: 6 }}>
            <UserCharts
              userId={userId}
              user={currentUser}
              padithi={filter.conditionType}
              // selectedYear={Number(selectedDay)}
              selectedMonth={filter?.month}
              selectedDay={
                filter?.predictionType === PredictionType.DAY
                  ? Number(filter?.day)
                  : null
              }
              predictionType={filter.predictionType}
            />
          </Box>
        )}

        {/* Filter form */}
        {userId && currentUser && (
          <>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              width="100%"
              className="mb-4"
            >
              <Box flex={1}>
                <FilterForm
                  isLoading={loading}
                  defaultValues={filter}
                  user={currentUser}
                  onSubmit={handleGetPredictions}
                />
              </Box>
              <UserProfileModal user={currentUser} />
            </Box>
            <AppDivider />
          </>
        )}

        {loading && <Loading text="Loading Predictions..." />}

        {!loading && predictionsData && (
          <Predictions
            user={currentUser}
            filter={filter}
            predictionData={predictionsData}
          />
        )}
        {showDataNotFound &&
          <DataNotFound message="No predictions found for the selected category and period." />
        }
      </Box>
      {/* Floating ChatBot */}
      <ChatBot currentUser={currentUser} />
    </>
  );
};

export default PredictionPage;
